import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { ApiError } from "../api/client";
import { changePassword } from "../api/content";
import { sessionName, signOutEverywhere, useSession } from "../session/SessionContext";

/** A signed-in member's account: who they are, a password change, and sign-out. */
export function AccountPage() {
  const { session, ready } = useSession();
  if (!ready) return null;
  if (session.kind === "ANONYMOUS") {
    return <Navigate to="/signin?next=/account" replace />;
  }

  return (
    <div className="page">
      <div className="container">
        <div className="member-box">
          <h1>Your account</h1>
          <p>
            Signed in as <strong>{sessionName(session)}</strong>
            {session.email && session.displayName ? ` (${session.email})` : ""}.
          </p>
          {session.kind === "ADMIN" ? (
            <p className="muted">
              Private and shared items are shown to you across the site, marked with a badge.
              Manage them in the <Link to="/admin">admin console</Link>.
            </p>
          ) : (
            <>
              <p className="muted">
                You can see everything shared with members, plus anything shared with you
                personally — look for the <span className="vis-badge" data-vis="RESTRICTED">Shared</span>{" "}
                and <span className="vis-badge" data-vis="MEMBERS">Members</span> badges.
              </p>
              <PasswordForm />
            </>
          )}
          <p>
            <button className="btn btn--ghost" onClick={() => void signOutEverywhere()}>
              Sign out
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}

function PasswordForm() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (next.length < 8) return setError("Choose a password of at least 8 characters.");
    if (next !== confirm) return setError("The new passwords don't match.");
    setBusy(true);
    setError("");
    try {
      await changePassword(current, next);
      setDone(true); // every session ended, this one included
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 401
          ? "Your current password is incorrect."
          : err instanceof Error
            ? err.message
            : "Could not change the password.",
      );
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <p className="notice notice--ok" role="status">
        Password changed. For safety you've been signed out everywhere —{" "}
        <a href="/signin?next=/account">sign in again</a> with the new one.
      </p>
    );
  }

  return (
    <form className="form" onSubmit={onSubmit} noValidate>
      <h2 style={{ fontSize: "1rem" }}>Change password</h2>
      {error && (
        <p className="notice notice--err" role="alert">
          ✕ {error}
        </p>
      )}
      <div className="field">
        <label htmlFor="pw-current">Current password</label>
        <input id="pw-current" type="password" value={current} autoComplete="current-password"
               onChange={(e) => setCurrent(e.target.value)} />
      </div>
      <div className="field">
        <label htmlFor="pw-new">New password</label>
        <input id="pw-new" type="password" value={next} autoComplete="new-password"
               onChange={(e) => setNext(e.target.value)} />
      </div>
      <div className="field">
        <label htmlFor="pw-confirm">New password again</label>
        <input id="pw-confirm" type="password" value={confirm} autoComplete="new-password"
               onChange={(e) => setConfirm(e.target.value)} />
      </div>
      <div>
        <button className="btn" type="submit" disabled={busy || !current || !next}>
          {busy ? "Saving…" : "Change password"}
        </button>
      </div>
    </form>
  );
}
